import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BarChart3, CalendarDays, Search, Check, ChevronDown, ChevronUp, ClipboardList, Info, ListFilter, Pencil, Plus, RotateCcw, Settings2, UserRound, X } from 'lucide-react';
import { Botao, classesBotao, Selo, type CorSelo } from '../../componentes/base';
import { CampoData, Entrada, Rotulo, Selecao } from '../../componentes/formulario';
import { Menu } from '../../componentes/menu';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { FiltrosSalvos } from '../../componentes/filtros-salvos';
import { Tabela, useDebounce, type Coluna, type SelecaoTabela } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { Vazio } from '../../componentes/estados';
import { get, getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, dinheiro, nomeParceiro, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { TIPOS_ATIVIDADE, type Atividade, type Funil, type TipoAtividade } from './tipos';
import { baixarTexto, FiltroMultiplo, iniciais } from './CrmComponentes';
import { BotaoAtalhoTarefas } from './AtalhoTarefas';
import { DialogoConcluir } from './ConcluirAtividades';
import { PaginaRelatorioCrm } from './PaginaRelatorioCrm';
import { CAMINHO_WORKFLOWS, FormTarefa, rascunhoDaAtividade, rascunhoInicial } from './FormTarefa';
import { ProximaTarefa, useWorkflows } from './ProximaTarefa';
import { adiar, csv, intervaloDoPeriodo, MODOS_ADIAR, PERIODOS, semanaAtual, type ModoAdiar, type Periodo } from './crm-sugestoes';
import { CAMINHO_RELATORIOS, caminhoDaNegociacao, caminhoDaTarefa, caminhoDoRelatorio } from './crm-caminhos';
import { AtaComArquivos } from './email-anexos';
import { DrawerOportunidade } from './DrawerOportunidade';
import { IconeTipoAtividade } from './tarefas-comum';
import { TarefasExcluidasGeral } from './TarefasExcluidas';
import { FalhaNoBloco, porBlocos, TETO_BUSCA } from '@malgoos/shared';

interface ResultadoLote {
  alterados: number;
  ignorados: number;
}
/** Rodada crm1 (ROB-M1): o servidor aceita até 50 por chamada; a seleção vai em blocos de 50 e o resultado é somado. */
async function emLotesDe50<T>(lista: readonly T[], chamar: (bloco: T[]) => Promise<ResultadoLote>): Promise<ResultadoLote> {
  const total = { alterados: 0, ignorados: 0 };
  for (const r of await porBlocos(lista, chamar)) {
    total.alterados += r.alterados;
    total.ignorados += r.ignorados;
  }
  return total;
}

/**
 * CRM no novo desenho, lote 2 (C-D, ficha 2026-10-26 "crm-novo-desenho"): a página Atividades do CRM, como a "Tarefas" do
 * clone do RD. Só as atividades das negociações (pergunta 4, resposta "só (a)"). Filtros por responsáveis (o principal ou um
 * dos atribuídos), período, tipos e situação; resumo da semana; seleção com "todos deste filtro"; ações em massa (adiar,
 * exportar, transferir, atribuir, concluir com a ata) com confirmação pela contagem; criar e editar tarefa; concluir com a ata e
 * "Criar próxima tarefa".
 */

// Lista de 02/11 (item 8): "excluidas" lista as tarefas da Lixeira (`/pipeline/atividades/excluidas`); "todas" inclui as excluídas abaixo da tabela.
const SITUACOES = ['atrasadas', 'pendentes', 'concluidas', 'excluidas', 'todas'] as const;
type Situacao = (typeof SITUACOES)[number];
const COR: Record<string, CorSelo> = { atrasada: 'laranja', hoje: 'azul', futura: 'neutro', concluida: 'verde', cancelada: 'neutro' };
const LIMITE_SELECAO = 500;

/**
 * Ajustes do CRM 27/10 (C24): os filtros ligados viram etiquetas removíveis abaixo da barra, como na origem. A situação
 * "pendentes" é o padrão da página e não vira etiqueta. Função pura, testada.
 */
export function etiquetasDosFiltros(f: { responsaveis: string[]; periodo: Periodo | ''; tipos: string[]; situacao: Situacao }) {
  return [
    ...f.responsaveis.map((id) => ({ chave: `resp-${id}`, tipo: 'responsavel' as const, valor: id })),
    ...(f.periodo ? [{ chave: 'periodo', tipo: 'periodo' as const, valor: f.periodo }] : []),
    ...f.tipos.map((x) => ({ chave: `tipo-${x}`, tipo: 'tipo' as const, valor: x })),
    ...(f.situacao !== 'pendentes' ? [{ chave: 'situacao', tipo: 'situacao' as const, valor: f.situacao }] : []),
  ];
}

function statusDa(a: Atividade, agora = new Date()): keyof typeof COR {
  if (a.estado === 'concluida') return 'concluida';
  if (a.estado === 'cancelada') return 'cancelada';
  if (a.atrasada) return 'atrasada';
  return new Date(a.data_hora).toDateString() === agora.toDateString() ? 'hoje' : 'futura';
}

export function PaginaAtividades() {
  // O relatório completo (crm5 E4) abre por "Ver relatório completo", como na origem: sub-rota das Atividades, sem item
  // próprio na barra lateral (val8: o CRM fica só com as seis páginas pedidas).
  const { pathname } = useLocation();
  if (pathname.replace(/\/+$/, '') === CAMINHO_RELATORIOS) return <PaginaRelatorioCrm />;
  return <ListaAtividades />;
}

function ListaAtividades() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const podeEditar = s.pode('pipeline.editar');
  // Val5 (29/10): transferir, atribuir e concluir em massa pedem `pipeline.em_massa`; exportar, `pipeline.exportar`; adiar segue
  // com `pipeline.editar` (uma a uma). A seleção aparece para quem faz alguma delas.
  const emMassa = s.pode('pipeline.em_massa');
  const podeExportar = s.pode('pipeline.exportar');
  const [sobre, setSobre] = useState<string | null>(null);
  const [responsaveis, setResponsaveis] = useState<string[]>([]);
  const [periodo, setPeriodo] = useState<Periodo | ''>('');
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');
  const [tipos, setTipos] = useState<string[]>([]);
  const [situacao, setSituacao] = useState<Situacao>('pendentes');
  const [painel, setPainel] = useState(false);
  const [semana, setSemana] = useState(false);
  const [funilSemana, setFunilSemana] = useState('');
  const [form, setForm] = useState<{ atual?: Atividade } | null>(null);
  const [concluir, setConcluir] = useState<Atividade[] | null>(null);
  const [proxima, setProxima] = useState<{ a: Atividade; ata: string } | null>(null);
  const [verAta, setVerAta] = useState<Atividade | null>(null);
  const [confirmar, setConfirmar] = useState<{ texto: string; executar: () => Promise<void> } | null>(null);
  const [executando, setExecutando] = useState(false);
  const membros = useQuery({ queryKey: ['membros', 'todos'], queryFn: () => getTodos<{ id: string; nome: string }>('/membros'), staleTime: 300_000 });
  const funis = useQuery({ queryKey: ['pipeline', 'funis'], queryFn: () => get<{ itens: Funil[] }>('/pipeline/funis') });
  const workflows = useWorkflows();

  const intervalo = intervaloDoPeriodo(periodo, new Date(), de, ate);
  const params = useMemo(
    () => ({ filtro: situacao === 'excluidas' ? 'todas' : situacao, tipos: tipos.length ? tipos.join(',') : undefined, responsaveis: responsaveis.length ? responsaveis.join(',') : undefined, de: intervalo.de, ate: intervalo.ate }),
    [situacao, tipos, responsaveis, intervalo.de, intervalo.ate],
  );
  const chaveFiltro = JSON.stringify(params);
  // Item 8: as excluídas valem os mesmos filtros (tipos, responsáveis, período). A busca da tabela é interna a ela; no filtro
  // "Excluídas", que não mostra a tabela, a busca é um campo próprio. Em "Todos os status" as excluídas não usam a busca.
  const [buscaExcl, setBuscaExcl] = useState('');
  const buscaExclD = useDebounce(buscaExcl).trim();
  const paramsExcl = useMemo(
    () => ({ tipos: params.tipos, responsaveis: params.responsaveis, de: params.de, ate: params.ate, busca: situacao === 'excluidas' && buscaExclD ? buscaExclD : undefined }),
    [params.tipos, params.responsaveis, params.de, params.ate, situacao, buscaExclD],
  );
  const [selecionados, setSelecionados] = useState<{ chave: string; ids: Set<string> }>({ chave: '', ids: new Set() });
  const marcados = selecionados.chave === chaveFiltro ? selecionados.ids : new Set<string>();
  const mudarSelecao = (f: (x: Set<string>) => Set<string>) => setSelecionados((a) => ({ chave: chaveFiltro, ids: f(a.chave === chaveFiltro ? a.ids : new Set()) }));
  const limpar = () => mudarSelecao(() => new Set());
  const selecao: SelecaoTabela<Atividade> | undefined = podeEditar || emMassa || podeExportar
    ? {
        marcados,
        rotulo: (a) => t('crm.atividades.selecionar', { nome: textoLivre(a.titulo, a.parceiro_nome) }),
        rotuloTodas: t('crm.atividades.selecionarPagina'),
        aoAlternar: (a, v) =>
          mudarSelecao((x) => {
            const n = new Set(x);
            if (v) n.add(a.id);
            else n.delete(a.id);
            return n;
          }),
        aoAlternarTodas: (linhas, v) =>
          mudarSelecao((x) => {
            const n = new Set(x);
            for (const l of linhas) {
              if (v) n.add(l.id);
              else n.delete(l.id);
            }
            return n;
          }),
      }
    : undefined;
  const doFiltro = useQuery({ queryKey: ['/pipeline/atividades', 'selecao', params], queryFn: () => get<{ total: number; itens: { id: string; data_hora: string }[] }>('/pipeline/atividades/selecao', params), enabled: marcados.size > 0 });
  const resumo = useQuery({
    queryKey: ['/pipeline/atividades', 'resumo-semana', funilSemana, responsaveis],
    queryFn: () => get<{ criadas: number; concluidas: number; abertas_em_dia: number; atrasadas: number }>('/pipeline/atividades/resumo-semana', { ...semanaAtual(), funil_id: funilSemana || undefined, responsaveis: responsaveis.length ? responsaveis.join(',') : undefined }),
    enabled: semana,
  });

  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: ['/pipeline/atividades'] });
    await qc.invalidateQueries({ queryKey: ['pipeline'] });
  };
  // Rodada crm2 (VIS-M1): o resumo do filtro de responsáveis passa pelo modo discreto.
  const nomeMembro = (id: string) => nomePessoa(membros.data?.itens.find((m) => m.id === id)?.nome ?? '');
  const ids = [...marcados];
  /** Confirmação pela contagem antes de qualquer ação em massa que muda dado. */
  const emLote = (texto: string, executar: () => Promise<{ alterados: number; ignorados: number } | void>) =>
    setConfirmar({
      texto,
      executar: async () => {
        let r: Awaited<ReturnType<typeof executar>>;
        try {
          r = await executar();
        } catch (e) {
          if (!(e instanceof FalhaNoBloco)) throw e;
          // Rodada crm2 (ROB-M1): blocos já gravados saem da seleção; repetir manda só o que faltou.
          const feitos = new Set(e.feitos.map((x) => (typeof x === 'string' ? x : (x as { id: string }).id)));
          await recarregar();
          mudarSelecao((s) => new Set([...s].filter((x) => !feitos.has(x))));
          toast.erro(t('crm.contagem.parcial', { feitos: e.feitos.length, total: e.total, erro: mensagemDeErro(e.causa) }));
          return;
        }
        await recarregar();
        if (r) toast.sucesso(t('crm.atividades.resultado', r));
        limpar();
      },
    });
  const lerSelecionadas = async () => (await getTodos<Atividade>('/pipeline/atividades', params)).itens.filter((a) => marcados.has(a.id));
  const acoesMassa = () => [
    ...MODOS_ADIAR.map((m: ModoAdiar) => ({
      chave: `adiar-${m}`,
      rotulo: `${t('crm.atividades.adiar')}: ${t(`crm.atividades.adiarModos.${m}`)}`,
      aoClicar: () =>
        emLote(t('crm.atividades.confirmarLote', { count: ids.length }), async () => {
          const datas = new Map((doFiltro.data?.itens ?? (await get<{ itens: { id: string; data_hora: string }[] }>('/pipeline/atividades/selecao', params)).itens).map((x) => [x.id, x.data_hora]));
          const itens = ids.filter((i) => datas.has(i)).map((i) => ({ id: i, data_hora: adiar(datas.get(i)!, m) }));
          return emLotesDe50(itens, (bloco) => post<ResultadoLote>('/pipeline/atividades/lote/adiar', { itens: bloco }));
        }),
    })),
  ];
  const escolherResponsavel = (modo: 'transferir' | 'atribuir') =>
    (membros.data?.itens ?? []).map((m) => ({
      chave: `${modo}-${m.id}`,
      rotulo: nomePessoa(m.nome),
      aoClicar: () =>
        emLote(`${t('crm.atividades.confirmarLote', { count: ids.length })} ${t(modo === 'transferir' ? 'crm.atividades.confirmarTransferir' : 'crm.atividades.confirmarAtribuir', { nome: nomePessoa(m.nome) })}`, () =>
          emLotesDe50(ids, (bloco) => post<ResultadoLote>(`/pipeline/atividades/lote/${modo}`, { ids: bloco, usuario_id: m.id })),
        ),
    }));
  const exportar = async () => {
    try {
      const linhas = await lerSelecionadas();
      const cab = [t('crm.atividades.colunas.tarefa'), t('crm.form.tipo'), t('crm.atividades.colunas.status'), t('crm.atividades.colunas.dataHora'), t('crm.atividades.colunas.responsaveis'), t('crm.atividades.colunas.negociacao'), t('crm.atividades.colunas.valor')];
      baixarTexto(
        t('crm.atividades.arquivoCsv'),
        csv([cab, ...linhas.map((a) => [a.titulo, t(`tipoAtividade.${a.tipo}`), t(`crm.atividades.status.${statusDa(a)}`), dataHora(a.data_hora), (a.responsaveis ?? []).map((r) => r.nome).join(', '), a.oportunidade_titulo, dinheiro(a.oportunidade_valor_centavos ?? 0, a.oportunidade_moeda)])]),
      );
      toast.sucesso(t('crm.atividades.exportado'));
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    }
  };

  const colunas: Coluna<Atividade>[] = [
    {
      chave: 'titulo',
      rotulo: t('crm.atividades.colunas.tarefa'),
      principal: true,
      // Texto simples: no celular a coluna principal fica dentro do botão do cartão (abre a tarefa pelo `aoClicar`).
      render: (a) => (
        <span className="inline-flex items-center gap-2" data-abrir-tarefa={a.id} title={t(`tipoAtividade.${a.tipo}`)}>
          <IconeTipoAtividade tipo={a.tipo} />
          <span className="sr-only">{t(`tipoAtividade.${a.tipo}`)}:</span>
          <span className="font-medium text-azul">{textoLivre(a.titulo, a.parceiro_nome)}</span>
        </span>
      ),
    },
    { chave: 'estado', rotulo: t('crm.atividades.colunas.status'), render: (a) => <Selo cor={COR[statusDa(a)]}>{t(`crm.atividades.status.${statusDa(a)}`)}</Selo> },
    { chave: 'data_hora', rotulo: t('crm.atividades.colunas.dataHora'), mono: true, render: (a) => <span className={a.atrasada ? 'text-laranja' : ''}>{dataHora(a.data_hora)}</span> },
    {
      chave: 'responsaveis',
      rotulo: t('crm.atividades.colunas.responsaveis'),
      render: (a) => (
        <span className="flex flex-wrap gap-1">
          {(a.responsaveis ?? []).map((r) => (
            <span key={r.id} title={nomePessoa(r.nome)} aria-label={nomePessoa(r.nome)} className="flex size-7 items-center justify-center rounded-full bg-sup-3 text-xs font-semibold text-tinta-2">
              {iniciais(r.nome)}
            </span>
          ))}
        </span>
      ),
    },
    {
      chave: 'oportunidade_titulo',
      rotulo: t('crm.atividades.colunas.negociacao'),
      render: (a) => (
        <span className="flex items-start gap-1">
          <span className="flex min-w-0 flex-col">
            <Link to={caminhoDaNegociacao(a.oportunidade_id)} className="text-acento-forte hover:underline" onClick={(e) => e.stopPropagation()}>
              {textoLivre(a.oportunidade_titulo, a.parceiro_nome)}
            </Link>
            {/* C24: o contato (cliente) da negociação sob o nome, como na origem. */}
            {a.parceiro_nome && <span className="text-xs text-tinta-2">{nomeParceiro(a.parceiro_nome)}</span>}
            {a.oportunidade_etapa_nome && <span className="text-xs text-tinta-3">{a.oportunidade_etapa_nome}</span>}
          </span>
          <button
            type="button"
            className="grid size-7 shrink-0 place-items-center rounded-full text-tinta-3 hover:bg-sup-3 hover:text-tinta-1"
            aria-label={t('crm.atividades.sobreNegociacao')}
            title={t('crm.atividades.sobreNegociacao')}
            data-sobre-negociacao={a.oportunidade_id}
            onClick={(e) => {
              e.stopPropagation();
              setSobre(a.oportunidade_id);
            }}
          >
            <Info className="size-4" aria-hidden />
          </button>
        </span>
      ),
    },
    { chave: 'valor', rotulo: t('crm.atividades.colunas.valor'), alinhar: 'direita', mono: true, render: (a) => dinheiro(a.oportunidade_valor_centavos ?? 0, a.oportunidade_moeda) },
    {
      chave: 'acoes',
      rotulo: tn('comum.acoes'),
      alinhar: 'direita',
      render: (a) =>
        a.estado === 'pendente' && podeEditar ? (
          <span className="inline-flex items-center gap-1">
            {/* C19 (decisão 10): a linha abre a ficha; editar vira o lápis. */}
            <Botao
              tamanho="sm"
              variante="fantasma"
              icone={<Pencil className="size-4" />}
              aria-label={t('crm.atividades.editarTarefa', { titulo: a.titulo })}
              title={t('crm.form.editarTitulo')}
              data-editar-linha={a.id}
              onClick={(e) => {
                e.stopPropagation();
                setForm({ atual: a });
              }}
            />
            <Botao
              tamanho="sm"
              icone={<Check className="size-4" />}
              data-concluir-linha={a.id}
              onClick={(e) => {
                e.stopPropagation();
                setConcluir([a]);
              }}
            >
              {t('acoes.concluir')}
            </Botao>
          </span>
        ) : a.estado === 'cancelada' && podeEditar ? (
          // Lista B de 30/10 (item 14): a tarefa cancelada reabre pela edição.
          <Botao
            tamanho="sm"
            variante="fantasma"
            icone={<RotateCcw className="size-4" />}
            aria-label={t('crm.ficha.reabrirTarefa', { titulo: textoLivre(a.titulo, a.parceiro_nome) })}
            data-reabrir-linha={a.id}
            onClick={(e) => {
              e.stopPropagation();
              setForm({ atual: a });
            }}
          >
            {t('crm.form.reabrir')}
          </Botao>
        ) : a.ata ? (
          <Botao
            tamanho="sm"
            variante="fantasma"
            onClick={(e) => {
              e.stopPropagation();
              setVerAta(a);
            }}
          >
            {t('crm.atividades.verAta')}
          </Botao>
        ) : null,
    },
  ];

  const nResp = responsaveis.length;
  const resumoResp = !nResp ? t('crm.atividades.todasTarefas') : nResp === 1 ? nomeMembro(responsaveis[0]!) : t('crm.atividades.nResponsaveis', { count: nResp });
  const resumoTipos = !tipos.length ? t('crm.atividades.todosTipos') : tipos.length === 1 ? t(`tipoAtividade.${tipos[0]}`) : t('crm.atividades.nTipos', { count: tipos.length });
  const semanaAgora = semanaAtual();
  const etiquetas = etiquetasDosFiltros({ responsaveis, periodo, tipos, situacao });
  const textoEtiqueta = (e: (typeof etiquetas)[number]) =>
    e.tipo === 'responsavel'
      ? nomeMembro(e.valor)
      : e.tipo === 'periodo'
        ? periodo === 'personalizado'
          ? `${t(`crm.atividades.periodos.${e.valor}`)}: ${de ? data(`${de}T00:00`) : '...'} - ${ate ? data(`${ate}T00:00`) : '...'}`
          : t(`crm.atividades.periodos.${e.valor}`)
        : e.tipo === 'tipo'
          ? t(`tipoAtividade.${e.valor}`)
          : e.valor === 'excluidas'
            ? t('crm.excluidas.filtro')
            : t(`crm.atividades.situacoes.${e.valor}`);
  const tirarEtiqueta = (e: (typeof etiquetas)[number]) => {
    if (e.tipo === 'responsavel') setResponsaveis((l) => l.filter((x) => x !== e.valor));
    else if (e.tipo === 'periodo') setPeriodo('');
    else if (e.tipo === 'tipo') setTipos((l) => l.filter((x) => x !== e.valor));
    else setSituacao('pendentes');
  };
  const total = doFiltro.data?.total ?? 0;

  return (
    <div className="flex min-w-0 flex-col gap-5" data-pagina-atividades="">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="kicker">{t('casca.comercial')}</p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">{t('crm.atividades.titulo')}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {podeEditar && (
            <Link to={CAMINHO_WORKFLOWS} className="flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-tinta-2 hover:bg-sup-2" data-link-workflows="">
              <Settings2 className="size-4" aria-hidden />
              {t('crm.atividades.configurar')}
            </Link>
          )}
          {/* Origem: o atalho de tarefas também no cabeçalho da página Tarefas. */}
          <BotaoAtalhoTarefas />
          {s.pode('pipeline.criar') && (
            <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setForm({})} data-criar-tarefa="">
              {t('crm.atividades.criar')}
            </Botao>
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2" data-filtros-atividades="">
        <div className="w-full sm:w-64">
          <FiltroMultiplo
            data-filtro-responsaveis=""
            rotulo={t('crm.atividades.responsaveis')}
            resumo={resumoResp}
            icone={<UserRound className="size-4 shrink-0 text-tinta-3" aria-hidden />}
            opcoes={(membros.data?.itens ?? []).map((m) => ({ valor: m.id, rotulo: nomePessoa(m.nome) }))}
            marcados={responsaveis}
            aoMudar={setResponsaveis}
            limpar={t('crm.atividades.limparFiltros')}
            extra={
              <button type="button" className="rounded px-2 py-1 text-left text-sm font-medium text-acento-forte hover:bg-sup-3" onClick={() => setResponsaveis([s.usuario.id])}>
                {t('crm.atividades.minhas')}
              </button>
            }
          />
        </div>
        <Botao icone={<ListFilter className="size-4" />} onClick={() => setPainel(true)} data-abrir-filtros="">
          {t('crm.atividades.filtrosN', { n: etiquetas.length })}
        </Botao>
        <div className="w-full sm:w-64">
          <FiltroMultiplo
            data-filtro-tipos=""
            rotulo={t('crm.form.tipo')}
            resumo={resumoTipos}
            icone={<ClipboardList className="size-4 shrink-0 text-tinta-3" aria-hidden />}
            opcoes={TIPOS_ATIVIDADE.map((x: TipoAtividade) => ({ valor: x, rotulo: t(`tipoAtividade.${x}`) }))}
            marcados={tipos}
            aoMudar={setTipos}
            limpar={t('crm.atividades.limparFiltros')}
          />
        </div>
        <div className="w-full sm:w-52">
          <Selecao aria-label={t('crm.atividades.situacao')} value={situacao} opcoes={SITUACOES.map((x) => ({ valor: x, rotulo: x === 'excluidas' ? t('crm.excluidas.filtro') : t(`crm.atividades.situacoes.${x}`) }))} onChange={(e) => setSituacao(e.target.value as Situacao)} data-filtro-situacao="" />
        </div>
      </div>

      {etiquetas.length > 0 && (
        <ul className="-mt-2 flex flex-wrap items-center gap-2" aria-label={t('crm.atividades.filtrosLigados')} data-etiquetas-atividades="">
          {etiquetas.map((e) => (
            <li key={e.chave} className="flex items-center gap-1 rounded-full border border-borda bg-sup-1 py-0.5 pr-1 pl-3 text-xs">
              {textoEtiqueta(e)}
              <button type="button" className="rounded-full p-1 hover:bg-sup-3" aria-label={t('crm.atividades.tirarFiltro', { filtro: textoEtiqueta(e) })} onClick={() => tirarEtiqueta(e)}>
                <X className="size-3" aria-hidden />
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              className="rounded px-2 py-1 text-xs font-medium text-acento-forte hover:underline"
              onClick={() => {
                setResponsaveis([]);
                setPeriodo('');
                setTipos([]);
                setSituacao('pendentes');
              }}
            >
              {t('crm.atividades.limparFiltros')}
            </button>
          </li>
        </ul>
      )}

      <section className="rounded-xl border border-borda bg-sup-1" data-resumo-semana="">
        <button type="button" aria-expanded={semana} className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-semibold" onClick={() => setSemana((x) => !x)}>
          {t('crm.atividades.resumoSemana')}
          {semana ? <ChevronUp className="size-4" aria-hidden /> : <ChevronDown className="size-4" aria-hidden />}
        </button>
        {semana && (
          <div className="flex flex-col gap-3 border-t border-borda px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-sm text-tinta-2">
                <CalendarDays className="size-4" aria-hidden />
                {t('crm.atividades.diasDaSemana', { de: data(semanaAgora.de), ate: data(new Date(Date.parse(semanaAgora.ate) - 1)) })}
              </p>
              <div className="w-full sm:w-56">
                <Selecao aria-label={t('crm.atividades.funil')} value={funilSemana} vazio={t('crm.atividades.todosFunis')} opcoes={(funis.data?.itens ?? []).filter((f) => !f.arquivado_em).map((f) => ({ valor: f.id, rotulo: f.nome }))} onChange={(e) => setFunilSemana(e.target.value)} />
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(
                [
                  ['criadas', resumo.data?.criadas],
                  ['concluidas', resumo.data?.concluidas],
                  ['abertasEmDia', resumo.data?.abertas_em_dia],
                  ['atrasadas', resumo.data?.atrasadas],
                ] as const
              ).map(([k, n]) => (
                <div key={k} className="rounded-lg bg-sup-2 px-3 py-2">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-tinta-3">{t(`crm.atividades.${k}`)}</dt>
                  <dd className={`numero text-2xl font-bold ${k === 'atrasadas' && n ? 'text-laranja' : ''}`} data-resumo={k}>
                    {n ?? '...'}
                  </dd>
                </div>
              ))}
            </dl>
            {s.pode('pipeline.ver') && (
              <div className="flex justify-end">
                <Botao icone={<BarChart3 className="size-4" />} onClick={() => navegar(caminhoDoRelatorio({ funil: funilSemana || undefined, responsavel: s.usuario.id }))} data-ver-relatorio="">
                  {t('crm.atividades.verRelatorio')}
                </Botao>
              </div>
            )}
          </div>
        )}
      </section>

      {marcados.size > 0 && situacao !== 'excluidas' && (
        <section aria-label={t('crm.atividades.acoesMassa')} className="flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-borda bg-sup-1 p-3" data-barra-selecao="">
          <p className="min-w-0 flex-1 basis-48 text-sm font-medium text-tinta-1">
            {t('crm.atividades.selecionados', { count: marcados.size })}{' '}
            {total > marcados.size && (
              <button type="button" className="font-medium text-acento-forte underline" data-selecionar-todos="" onClick={() => mudarSelecao(() => new Set((doFiltro.data?.itens ?? []).map((x) => x.id)))}>
                {total > LIMITE_SELECAO ? t('crm.atividades.selecionarTodosLimite', { count: total, limite: LIMITE_SELECAO }) : t('crm.atividades.selecionarTodos', { count: total })}
              </button>
            )}
          </p>
          <Botao variante="fantasma" tamanho="sm" onClick={limpar}>
            {t('crm.atividades.limparSelecao')}
          </Botao>
          {podeEditar && <Menu rotulo={t('crm.atividades.adiar')} itens={acoesMassa()} classeGatilho={classesBotao('secundario', 'sm')} gatilho={<>{t('crm.atividades.adiar')}<ChevronDown className="size-4" aria-hidden /></>} />}
          {podeExportar && (
            <Botao tamanho="sm" onClick={() => void exportar()} data-exportar="">
              {t('crm.atividades.exportar')}
            </Botao>
          )}
          {emMassa && (
            <>
              <Menu rotulo={t('crm.atividades.transferir')} itens={escolherResponsavel('transferir')} classeGatilho={classesBotao('secundario', 'sm')} gatilho={<>{t('crm.atividades.transferir')}<ChevronDown className="size-4" aria-hidden /></>} />
              <Menu rotulo={t('crm.atividades.atribuir')} itens={escolherResponsavel('atribuir')} classeGatilho={classesBotao('secundario', 'sm')} gatilho={<>{t('crm.atividades.atribuir')}<ChevronDown className="size-4" aria-hidden /></>} />
              <Botao
                tamanho="sm"
                variante="primario"
                data-concluir-massa=""
                onClick={() =>
                  void lerSelecionadas()
                    .then((l) => setConcluir(l.filter((a) => a.estado === 'pendente')))
                    .catch((e) => toast.erro(mensagemDeErro(e)))
                }
              >
                {t('crm.atividades.concluir')}
              </Botao>
            </>
          )}
        </section>
      )}

      {situacao === 'excluidas' && (
        <div className="flex flex-col gap-3" data-atividades-excluidas="">
          <div className="relative min-w-0 max-w-md">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
            <Entrada aria-label={tn('comum.buscar')} placeholder={tn('comum.buscar')} className="pl-9" value={buscaExcl} maxLength={TETO_BUSCA} onChange={(e) => setBuscaExcl(e.target.value)} data-busca-excluidas="" />
          </div>
          <TarefasExcluidasGeral params={paramsExcl} vazio={t('crm.excluidas.geralVazio')} />
        </div>
      )}
      {situacao !== 'excluidas' && <Tabela<Atividade>
        tela="pipeline.crm-atividades"
        caminho="/pipeline/atividades"
        params={params}
        filtros={{ situacao, tipos: tipos.join(','), responsaveis: responsaveis.join(','), periodo, de, ate }}
        aoAplicarFiltros={(f) => {
          setSituacao(SITUACOES.find((x) => x === f.situacao) ?? 'pendentes');
          setTipos(typeof f.tipos === 'string' && f.tipos ? f.tipos.split(',') : []);
          setResponsaveis(typeof f.responsaveis === 'string' && f.responsaveis ? f.responsaveis.split(',') : []);
          setPeriodo(PERIODOS.find((x) => x === f.periodo) ?? '');
          setDe(typeof f.de === 'string' ? f.de : '');
          setAte(typeof f.ate === 'string' ? f.ate : '');
        }}
        colunas={colunas}
        selecao={selecao}
        aoClicar={(a) => navegar(caminhoDaTarefa(a.oportunidade_id))}
        vazio={<Vazio texto={t('crm.atividades.vazio')} />}
      />}
      {situacao === 'todas' && <TarefasExcluidasGeral params={paramsExcl} titulo={t('crm.excluidas.geralTitulo')} vazio={t('crm.excluidas.geralVazio')} />}

      <PainelFiltros
        aberto={painel}
        aoFechar={() => setPainel(false)}
        atual={{ situacao, tipos: tipos.join(','), responsaveis: responsaveis.join(','), periodo, de, ate }}
        aoAplicar={(f) => {
          setSituacao(SITUACOES.find((x) => x === f.situacao) ?? 'pendentes');
          setTipos(f.tipos ? f.tipos.split(',') : []);
          setResponsaveis(f.responsaveis ? f.responsaveis.split(',') : []);
          setPeriodo(PERIODOS.find((x) => x === f.periodo) ?? '');
          setDe(f.de ?? '');
          setAte(f.ate ?? '');
          setPainel(false);
        }}
      />
      {form && (
        <FormTarefa
          atual={form.atual}
          inicial={form.atual ? rascunhoDaAtividade(form.atual) : rascunhoInicial(s.usuario.id)}
          aoFechar={() => setForm(null)}
          aoPedirConcluir={(a) => setConcluir([a])}
        />
      )}
      {concluir && concluir.length > 0 && (
        <DialogoConcluir
          atividades={concluir}
          aoFechar={() => setConcluir(null)}
          aoConcluir={({ ata }) => {
            if (concluir.length === 1 && (workflows.data?.abrir_proxima ?? true)) setProxima({ a: concluir[0]!, ata });
          }}
        />
      )}
      {proxima && <ProximaTarefa concluida={proxima.a} ata={proxima.ata} aoFechar={() => setProxima(null)} />}
      {sobre && <DrawerOportunidade id={sobre} aoFechar={() => setSobre(null)} aoMudar={() => void recarregar()} />}
      {verAta && <VerAta atividade={verAta} aoFechar={() => setVerAta(null)} />}
      <Confirmar
        aberto={!!confirmar}
        aoFechar={() => setConfirmar(null)}
        carregando={executando}
        titulo={t('crm.atividades.acoesMassa')}
        texto={confirmar?.texto ?? ''}
        aoConfirmar={() => {
          if (!confirmar) return;
          setExecutando(true);
          void confirmar
            .executar()
            .catch((e) => toast.erro(mensagemDeErro(e)))
            .finally(() => {
              setExecutando(false);
              setConfirmar(null);
            });
        }}
      />
    </div>
  );
}

/** Tela das visões salvas dos filtros da página Tarefas (uma tela por página, no `/visoes` do núcleo). */
export const TELA_FILTROS_ATIVIDADES = 'pipeline.crm-atividades-filtros';
type FiltrosPainel = { situacao: string; tipos: string; responsaveis: string; periodo: string; de: string; ate: string };

/**
 * Ajustes do CRM 28/10 (L10, C24): o drawer "Filtros (n)" da origem, com os filtros salvos (visões do núcleo) e o período de
 * agendamento. Os filtros inline continuam na barra; o período só mora aqui e aparece como etiqueta.
 */
function PainelFiltros({ aberto, aoFechar, atual, aoAplicar }: { aberto: boolean; aoFechar: () => void; atual: FiltrosPainel; aoAplicar: (f: FiltrosPainel) => void }) {
  const { t } = useTranslation('pipeline');
  const [f, setF] = useState<FiltrosPainel>(atual);
  const [anterior, setAnterior] = useState(aberto);
  if (aberto !== anterior) {
    setAnterior(aberto);
    if (aberto) setF(atual);
  }
  const completo = (x: Record<string, string>): FiltrosPainel => ({ situacao: x.situacao ?? 'pendentes', tipos: x.tipos ?? '', responsaveis: x.responsaveis ?? '', periodo: x.periodo ?? '', de: x.de ?? '', ate: x.ate ?? '' });
  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('crm.atividades.filtrosTitulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={() => aoAplicar(completo({}))}>
            {t('crm.atividades.limparFiltros')}
          </Botao>
          <Botao variante="primario" data-aplicar-filtros="" onClick={() => aoAplicar(f)}>
            {t('crm.atividades.aplicarFiltros')}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-painel-filtros-atividades="">
        <FiltrosSalvos tela={TELA_FILTROS_ATIVIDADES} filtros={f} aoAplicar={(x) => aoAplicar(completo(x))} />
        <Rotulo id="fa-periodo" rotulo={t('crm.atividades.periodoAgendamento')}>
          <Selecao
            id="fa-periodo"
            value={f.periodo}
            vazio={t('crm.atividades.qualquerPeriodo')}
            opcoes={PERIODOS.map((p) => ({ valor: p, rotulo: t(`crm.atividades.periodos.${p}`) }))}
            onChange={(e) => setF((x) => ({ ...x, periodo: e.target.value }))}
            data-filtro-periodo=""
          />
        </Rotulo>
        {f.periodo === 'personalizado' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <CampoData type="date" aria-label={t('crm.atividades.de')} value={f.de} onChange={(e) => setF((x) => ({ ...x, de: e.target.value }))} />
            <CampoData type="date" aria-label={t('crm.atividades.ate')} value={f.ate} onChange={(e) => setF((x) => ({ ...x, ate: e.target.value }))} />
          </div>
        )}
      </div>
    </Drawer>
  );
}

function VerAta({ atividade: a, aoFechar }: { atividade: Atividade; aoFechar: () => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.atividades.ata')}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <div className="flex flex-col gap-2 text-sm" data-ver-ata="">
        <AtaComArquivos oportunidadeId={a.oportunidade_id} atividade={a} parceiro={a.parceiro_nome} />
      </div>
    </Modal>
  );
}
